/**
 * Tab DOM Component
 *
 * Renders a single tab button using ISM-specific BEM classes.
 * No Redux, no business logic — receives all values as parameters.
 *
 * Structure:
 *   <button ..__tabs-button [--active]>
 *     <span ..__tabs-label>Floor Plans</span>
 *     <span ..__tabs-badge [--inactive]>2</span>
 *   </button>
 *
 * @module nativeInteractiveSiteMap/components/tab.dom
 */

import fastdom from 'fastdom';

const BUTTON_CLASS = 'nhs-c-native-interactive-site-map__tabs-button';
const BUTTON_ACTIVE_CLASS = `${BUTTON_CLASS}--active`;
const BUTTON_MQ_LARGE_CLASS = 'nhs-mq-native-interactive-site-map__tabs-button-large';
const LABEL_CLASS = 'nhs-u-text--no-break';
const BADGE_CLASS = 'nhs-c-native-interactive-site-map__tabs-badge';

/**
 * @param {object} params
 * @param {string}  params.label    - Display text for the tab (e.g. 'Floor Plans')
 * @param {number}  params.count    - Initial count to display in the badge
 * @param {string}  params.tabValue - Store value identifying this tab (e.g. 'floorPlans')
 * @param {boolean} params.isActive - Whether this tab is initially active
 * @returns {{ getElement: Function, setActive: Function, setCount: Function, remove: Function }}
 */
export default function createTabDom({ label, count, tabValue, isActive }) {
  const state = { root: null };

  function getBadge() {
    return state.root?.querySelector('[data-tab-count]');
  }

  function initializeDom() {
    const wrapper = document.createElement('div');
    wrapper.innerHTML = `<button
      type="button"
      class="${BUTTON_CLASS} ${BUTTON_MQ_LARGE_CLASS}${isActive ? ` ${BUTTON_ACTIVE_CLASS}` : ''}"
      data-tab-value="${tabValue}"
      role="tab"
      aria-selected="${isActive ? 'true' : 'false'}">
      <span class="${LABEL_CLASS}">${label}</span>
      <span class="${BADGE_CLASS}" data-tab-count>${count}</span>
    </button>`;
    state.root = wrapper.firstElementChild;
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    setActive(active) {
      fastdom.mutate(() => {
        state.root?.classList.toggle(BUTTON_ACTIVE_CLASS, active);
        state.root?.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    },

    setCount(newCount) {
      fastdom.mutate(() => {
        const badge = getBadge();
        if (badge) {
          badge.textContent = newCount;
        }
      });
    },

    remove() {
      const root = state.root;
      state.root = null;
      fastdom.mutate(() => { root?.remove(); });
    },
  };
}
